/**
 * The browser's remembered gym, and who it was remembered for.
 *
 * `localStorage.currentOrganizationId` has always been written by several
 * bootstraps — OrganizationContext, the mobile WebView sync, the mobile chat
 * page, demo mode — and read by anything that needs a gym before
 * OrganizationContext resolves. What it never recorded is WHICH signed-in user
 * it was written for, so it silently outlived a change of user in the same
 * browser. #4571: a CheerVue "Sign in as" support session opened still holding
 * the previous gym's id, and the assistant sent it as `x-organization-id`,
 * earning a 403 from the server org guard a second after sign-in.
 *
 * `currentOrganizationOwnerId` is that missing half. It is advisory, not a
 * security control — the server guard remains the only thing that decides what
 * a caller may read. Callers that need certainty should still prefer
 * `OrganizationContext.currentOrganization`; this is for the window before it
 * resolves.
 *
 * Writers that know the signed-in user should call `rememberOrganization` with
 * it. A value with no owner recorded predates this (or came from a bootstrap
 * that has no user id yet, such as the legacy `?mobile=true&orgId=` URL), and
 * `readRememberedOrganization` reports it as `ownerUnknown` so the caller can
 * decide — rather than guessing on the reader's behalf.
 */

export const REMEMBERED_ORG_KEY = 'currentOrganizationId';
export const REMEMBERED_ORG_OWNER_KEY = 'currentOrganizationOwnerId';
/**
 * The caller's other gyms, written next to the remembered gym by
 * OrganizationContext and the mobile WebView bootstraps and sent to the
 * assistant as `x-all-organization-ids`. Forgotten together with the
 * remembered gym: a list left by a different user is stale for exactly the
 * same reason the single id is, and OrganizationContext rewrites it as soon as
 * it resolves the current user's gyms.
 */
export const REMEMBERED_ALL_ORGS_KEY = 'allOrganizationIds';

export interface RememberedOrganization {
  organizationId: string;
  /**
   * True only when an owner was recorded AND it is the user asking. This is the
   * one positive signal here: everything else is an absence of evidence, and a
   * caller that reads "not someone else's" as "mine" fails open. False whenever
   * the asking user is unknown, however the value was stamped.
   */
  ownedByCurrentUser: boolean;
  /** True when no owner was recorded — the value may predate the current user. */
  ownerUnknown: boolean;
  /** True when an owner WAS recorded and it is not the user asking. */
  ownedBySomeoneElse: boolean;
}

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    // Safari private mode and embedded WebViews can throw on access alone.
    return null;
  }
}

/** Record the gym this browser is pointed at, and the user it was chosen by. */
export function rememberOrganization(organizationId: string, ownerId?: string | null): void {
  const store = storage();
  if (!store || !organizationId) return;
  try {
    store.setItem(REMEMBERED_ORG_KEY, organizationId);
    if (ownerId) {
      store.setItem(REMEMBERED_ORG_OWNER_KEY, ownerId);
    } else {
      // No user id at this call site. Drop any previous owner rather than
      // leaving a stamp that now describes the wrong value.
      store.removeItem(REMEMBERED_ORG_OWNER_KEY);
    }
  } catch {
    // Quota or a locked-down WebView: the remembered gym is a convenience.
  }
}

/**
 * Forget the remembered gym and its owner together — never one without the
 * other, or the next raw write would pair a fresh gym with a departed user's
 * name.
 *
 * `includeOrganizationList` also drops the sibling gym list, and is for one
 * situation only: the value turned out to belong to a DIFFERENT user, so their
 * gym list is stale for exactly the same reason. It is deliberately opt-in.
 * Most callers here are clearing an id that is merely invalid for the current
 * user — a saved gym they no longer have access to — moments after
 * OrganizationContext wrote a correct list beside it, and taking that list with
 * it would cost a multi-gym parent their cross-gym assistant scope.
 */
export function forgetRememberedOrganization(
  { includeOrganizationList = false }: { includeOrganizationList?: boolean } = {},
): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(REMEMBERED_ORG_KEY);
    store.removeItem(REMEMBERED_ORG_OWNER_KEY);
    if (includeOrganizationList) store.removeItem(REMEMBERED_ALL_ORGS_KEY);
  } catch {
    // See rememberOrganization.
  }
}

/**
 * Read the remembered gym as it relates to `currentUserId`. Returns null when
 * nothing is stored; otherwise the caller gets the id plus what is known about
 * who put it there.
 */
export function readRememberedOrganization(
  currentUserId?: string | null,
): RememberedOrganization | null {
  const store = storage();
  if (!store) return null;
  let organizationId: string | null = null;
  let ownerId: string | null = null;
  try {
    organizationId = store.getItem(REMEMBERED_ORG_KEY);
    ownerId = store.getItem(REMEMBERED_ORG_OWNER_KEY);
  } catch {
    return null;
  }
  if (!organizationId) return null;
  return {
    organizationId,
    ownedByCurrentUser: !!ownerId && !!currentUserId && ownerId === currentUserId,
    ownerUnknown: !ownerId,
    ownedBySomeoneElse: !!ownerId && !!currentUserId && ownerId !== currentUserId,
  };
}
